/**
 * Active-workout chooser — shown on the Workouts → Today tab when
 * `?choose=1` is set (the sidebar "Active workout" item routes here when
 * more than one in-progress session exists; see HealthWorkouts.tsx and
 * __root.tsx's dynamic nav item).
 *
 * Each row shows enough to disambiguate a morning vs. evening session at
 * a glance: name, start time, elapsed, and sets done/total. Clicking a
 * row pins that session via `?session=<id>`, which HealthWorkouts.tsx
 * picks up and feeds into the tracker.
 *
 * Sets done/total isn't on the list DTO (the `/workouts` list endpoint
 * doesn't include sets — only `/workouts/:id` does), so each row fetches
 * its own session detail. The active-session list is expected to stay in
 * the low single digits, so N+1 detail fetches are cheap and simpler than
 * an API change.
 */

import { useWorkoutSession, type WorkoutSessionDto } from '../../../api/health';
import { formatElapsed } from './elapsed';
import styles from './workouts.module.css';

export function ActiveWorkoutChooser({
  sessions,
  isLoading,
  onChoose,
}: {
  sessions: WorkoutSessionDto[];
  isLoading: boolean;
  onChoose: (sessionId: string) => void;
}) {
  if (isLoading) {
    return <div style={{ padding: 24, color: 'var(--ink-3)' }}>Loading active workouts…</div>;
  }

  if (sessions.length === 0) {
    return (
      <div
        style={{
          padding: '24px 20px',
          border: '1px dashed var(--line)',
          borderRadius: 10,
          background: 'var(--surface-2)',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>
          No active workouts right now.
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3
        style={{
          margin: '0 0 10px',
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--ink-3)',
        }}
      >
        Choose an active workout
      </h3>
      {sessions.map((s) => (
        <ChooserRow key={s.id} session={s} onChoose={onChoose} />
      ))}
    </div>
  );
}

function ChooserRow({
  session,
  onChoose,
}: {
  session: WorkoutSessionDto;
  onChoose: (sessionId: string) => void;
}) {
  const detailQ = useWorkoutSession(session.id);
  const totalSets = detailQ.data?.sets.length ?? null;
  const doneSets = detailQ.data?.sets.filter((s) => s.status === 'completed').length ?? null;
  const startLabel = session.startTime
    ? new Date(session.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <div
      className={styles.histRow}
      role="button"
      tabIndex={0}
      onClick={() => onChoose(session.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChoose(session.id);
        }
      }}
    >
      <div className={styles.histDate}>
        <div className={styles.histTime}>{startLabel}</div>
      </div>
      <div>
        <div className={styles.histName}>{session.name}</div>
        <div className={styles.histMeta}>
          {session.type} ·{' '}
          {session.pausedAt ? '⏸ Paused' : `${formatElapsed(session.startTime)} elapsed`}
          {totalSets !== null && (
            <>
              {' '}
              · {doneSets}/{totalSets} sets
            </>
          )}
        </div>
      </div>
    </div>
  );
}
